import { EDIT_COMMENT } from '@core/constants/analytics';
import trackMaterialComment from './trackMaterialComment';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Icons,
  Modal,
  Text,
  Tooltip,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { cleanState } from '@ublend-npm/aula-editor';
import { COMMENT_ITEM_TYPE } from '@core/constants/analytics';
import { API_URL, FILE_URL } from '@core/constants/endpoints';
import { LtiProvider } from '@ublend-npm/aula-schema';
import { CommentEntityType } from '@core/utils/commentsEntityTypes';
import useEditComment from '@core/comments/hooks/useEditComment';
import isEmptyContent from '@app/components/presentational/ClassRoom/Feed/isEmptyContent';
import useEnabledLTIIntegrations from '@app/hooks/useEnabledLTIIntegrations';
import { Comment } from '../../../../Comments/types';

interface EditCommentModalProps {
  open: boolean;
  onClose: () => void;
  comment: Comment;
  entityType: CommentEntityType;
  entityId: string;
  // From container
  EditorComponent: React.ComponentType<any>;
  accessToken: string;
  spaceId: string;
  institution: string;
  ltiProviders?: Array<LtiProvider>;
  isEducator: boolean;
  isAdmin: boolean;
  isRbacEnabled: boolean;
  user: any;
}

const EditCommentModal = ({
  open,
  onClose,
  comment,
  entityType,
  entityId,
  EditorComponent,
  accessToken,
  spaceId,
  institution,
  ltiProviders,
  isEducator,
  isAdmin,
  isRbacEnabled,
  user,
}: EditCommentModalProps) => {
  const theme = useTheme();
  const { palette } = theme;
  const editorRef = useRef<any>(null);
  const { mutateAsync: editComment, isLoading: isSaving } =
    useEditComment(comment);

  const getParsedContent = () => {
    try {
      const parsed = JSON.parse(comment.content);
      if (Array.isArray(parsed) && parsed.length > 0)
        return { ...cleanState, value: parsed };
      if (parsed && typeof parsed === 'object' && parsed.value) return parsed;
      return cleanState;
    } catch {
      return cleanState;
    }
  };

  const [editorState, setEditorState] = useState(() => getParsedContent());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canSubmit, setCanSubmit] = useState(true);
  const [liveMessage, setLiveMessage] = useState('');

  // Reset editor state each time the modal opens
  React.useEffect(() => {
    if (open) {
      setEditorState(getParsedContent());
      setLiveMessage('');
    }
  }, [open, comment.content]);

  const handleClose = () => {
    setEditorState(getParsedContent());
    onClose();
  };

  const editorConfig = useMemo(
    () => ({
      filesUrl: FILE_URL(),
      apiUrl: API_URL(),
      accessToken,
      spaceId,
      institution,
      ltiProviders,
      itemType: COMMENT_ITEM_TYPE,
      itemId: comment.id,
      isEducator,
      isAdmin,
      isRbacEnabled,
      userId: user?.objectId,
    }),
    [
      accessToken,
      spaceId,
      institution,
      ltiProviders,
      comment.id,
      isEducator,
      isAdmin,
      isRbacEnabled,
      user?.objectId,
    ]
  );

  const filteredEditorConfig = useEnabledLTIIntegrations(editorConfig);

  const handleEditorChange = useCallback((state: any) => {
    setEditorState(state);
  }, []);

  const handleFilesStatusChange = useCallback(
    ({ isUpdating: updating, isPersistable, hasError }: any) => {
      setCanSubmit(isPersistable && !updating && !hasError);
    },
    []
  );

  const loading = isSubmitting || isSaving;
  const submitDisabled = loading || isEmptyContent(editorState) || !canSubmit;

  const handleSave = async () => {
    try {
      await editComment({ content: JSON.stringify(editorState) });
      trackMaterialComment(EDIT_COMMENT, {
        itemId: entityId,
        itemType: 'Material',
        commentId: comment.id,
        entityType,
        spaceId,
        isRbacEnabled,
      });
      setLiveMessage('The comment has been saved');
      onClose();
    } catch {
      setLiveMessage('The comment could not be saved');
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="edit-comment-modal-title"
      aria-describedby="edit-comment-modal-description"
    >
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-comment-modal-title"
        sx={{
          width: { xs: 'calc(100% - 34px)', md: '760px' },
          height: 'auto',
          bgcolor: palette.background.paper,
          border: `1px solid ${palette.divider}`,
          borderRadius: '8px',
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '24px',
          gap: '8px',
          outline: 'none',
          boxSizing: 'border-box',
          maxHeight: { xs: 'calc(100vh - 100px)', md: 'calc(100vh - 40px)' },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            width: '100%',
            height: '44px',
            padding: 0,
            gap: '21px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '40px',
              marginTop: '2px',
            }}
          >
            <Text
              id="edit-comment-modal-title"
              component="h2"
              tabIndex={-1}
              sx={{
                fontWeight: 500,
                fontSize: '20px',
                lineHeight: '160%',
                letterSpacing: '0.15px',
                color: palette.text.primary,
                outline: 'none',
                margin: 0,
                padding: 0,
              }}
            >
              Edit comment
            </Text>
          </Box>

          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Close edit comment dialog"
            sx={{
              width: '40px',
              height: '40px',
              padding: '8px',
              borderRadius: '100px',
              flexShrink: 0,
              color: palette.text.secondary,
            }}
          >
            <Tooltip title="Close">
              <Icons.Close sx={{ width: '24px', height: '24px' }} />
            </Tooltip>
          </IconButton>
        </Box>

        {/* Body — editor + actions */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            width: '100%',
            gap: '24px',
          }}
        >
          {/* Editor area */}
          <Box
            id="edit-comment-modal-description"
            sx={{
              width: '100%',
              minHeight: '64px',
              maxHeight: 'calc(100vh - 300px)',
              overflowY: 'auto',
              overflowX: 'hidden',
              boxSizing: 'border-box',
              borderRadius: '8px',
              fontFamily: theme.typography.fontFamily,
              '& *': {
                fontFamily: `${theme.typography.fontFamily} !important`,
              },
              '& [data-testid="code-block"] *': {
                fontFamily: 'monospace !important',
              },
              '& .katex, & .katex *': {
                fontFamily: 'KaTeX_Main, serif !important',
              },
            }}
          >
            {open && (
              <Box
                sx={{
                  width: 'calc(100% - 16px)',
                  boxSizing: 'border-box',
                  marginTop: '16px',
                  margin: '24px 24px 4px 4px',
                  overflow: 'visible',
                }}
              >
                <EditorComponent
                  ref={editorRef}
                  config={filteredEditorConfig}
                  editorState={editorState}
                  onChange={handleEditorChange}
                  isSubmitting={loading}
                  setSubmittingState={setIsSubmitting}
                  isAssignmentsEnabled
                  type="comment"
                  onFilesStatusChange={handleFilesStatusChange}
                />
              </Box>
            )}
          </Box>

          {/* Actions row */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '16px',
              width: '100%',
              height: '42px',
            }}
          >
            <Button
              kind="secondary"
              onClick={handleClose}
              disabled={loading}
              aria-label="Cancel editing comment"
              sx={{
                width: '94px',
                height: '42px',
                padding: '8px 22px',
                borderRadius: '4px',
                fontWeight: 500,
                fontSize: '15px',
                lineHeight: '26px',
                textTransform: 'none',
                minWidth: 'unset',
                boxSizing: 'border-box',
                '&:hover': {
                  borderColor: palette.primary.dark,
                  backgroundColor: palette.action.hover,
                },
              }}
            >
              Cancel
            </Button>

            <Button
              kind="primary"
              onClick={handleSave}
              disabled={submitDisabled}
              aria-label={isSaving ? 'Saving comment' : 'Save comment'}
              sx={{
                width: '79px',
                height: '42px',
                padding: '8px 22px',
                borderRadius: '4px',
                fontWeight: 500,
                fontSize: '15px',
                lineHeight: '26px',
                textTransform: 'none',
                minWidth: 'unset',
                boxSizing: 'border-box',
                '&:hover': {
                  backgroundColor: palette.primary.dark,
                },
              }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>

        {/* Screen reader live region */}
        <Box
          aria-live="polite"
          role="status"
          sx={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
          }}
        >
          {liveMessage}
        </Box>
      </Box>
    </Modal>
  );
};

export default EditCommentModal;
